import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    const element = location.pathname === '/' ? document.getElementById(sectionId) : null;
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Desde otra página (ej. /socios): volver al inicio y bajar a la sección
      navigate(`/#${sectionId}`);
    }
  };

  const handleNavigation = (item) => {
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
    } else if (item.path) {
      setIsMobileMenuOpen(false);
      navigate(item.path);
    } else {
      scrollToSection(item.id);
    }
  };

  const navItems = [
    { label: 'Inicio', id: 'hero' },
    { label: 'Servicios', id: 'benefits' },
    { label: 'Actividades', id: 'services' },
    { label: 'Nuestros socios', id: 'nuestros-socios' },
    { label: 'Comisión Directiva', path: '/comision-directiva' },
    { label: 'Contacto', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        isScrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      {/* Enlace para saltar el menú: solo se ve al navegar con el teclado (tecla Tab) */}
      <a
        href="#contenido-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-blue-700 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:outline-none focus:ring-2 focus:ring-white"
      >
        Saltar al contenido
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-3 lg:py-4">
          {/* Logo */}
          <div className="flex items-center space-x-2 sm:space-x-3 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <img 
                src="/img/logo-uiq.webp" 
                alt="Logo UIQ"
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
              />
            </div>
            <div>
              <span className="block text-base sm:text-lg font-bold text-gray-900 leading-tight">Unión Industrial Quilmes</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id || item.label}
                onClick={() => handleNavigation(item)}
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 text-sm"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* CTA Button Desktop */}
          <div className="hidden lg:block">
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 transition-all duration-300 hover:shadow-lg"
            >
              Asociate ahora
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={isMobileMenuOpen}
            className="lg:hidden p-2.5 -mr-2 text-gray-700 hover:text-blue-600 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden py-3 border-t border-gray-200 bg-white max-h-[calc(100vh-72px)] overflow-y-auto">
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id || item.label}
                  data-testid={`mobile-nav-${(item.id || item.label).toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleNavigation(item)}
                  className="text-left text-gray-700 hover:text-blue-600 font-medium py-3 px-4 hover:bg-gray-50 active:bg-blue-50 rounded transition-colors text-base"
                >
                  {item.label}
                </button>
              ))}
              <Button
                data-testid="mobile-nav-cta"
                onClick={() => scrollToSection('contact')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold w-full mt-3 py-5"
              >
                Asociate ahora
              </Button>
            </nav>
          </div>
        )}
      </div>
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 top-[72px] bg-black/30 -z-10"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};
